import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Chip, Icon, Toast } from "@legal-500/legal500-design-system";
import type { Column, ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { DataTable } from "@/components/DataTable/DataTable";
import {
  campaignsService,
  type Campaign,
  type SegmentRunRecord,
  type SegmentWithProgress,
} from "@/api/campaigns.service";
import {
  segmentCopyDetails,
  segmentDisplayChip,
  segmentEmailedHeadline,
  segmentExceptionLines,
  segmentResumeErrorMessage,
  segmentResumedMessage,
  segmentRunPeriod,
  segmentRunStatusLabel,
  segmentScheduleDisplay,
} from "./campaign-display";
import { ROUTES } from "@/routes";
import styles from "./MailingGroups.module.css";

// A send is in flight while it waits for the worker (`scheduled` — including
// straight after a resume) or while the worker is dispatching it (`sending`).
// The page polls only in those two states; draft/sent/canceled are terminal.
const isInFlight = (status: string): boolean => status === "scheduled" || status === "sending";

const POLL_INTERVAL_MS = 2000;

const runColumns: Column<SegmentRunRecord>[] = [
  {
    header: "Outcome",
    accessor: "status",
    render: (_v, row) => segmentRunStatusLabel(row.status),
  },
  {
    header: "When",
    accessor: "started_at",
    render: (_v, row) => segmentRunPeriod(row),
  },
  {
    header: "Sent",
    accessor: "submitted",
    render: (_v, row) => `${row.submitted.toLocaleString()} / ${row.total.toLocaleString()}`,
  },
  {
    header: "Failed",
    accessor: "failed",
    render: (_v, row) => row.failed.toLocaleString(),
  },
  {
    header: "Suppressed",
    accessor: "suppressed",
    render: (_v, row) => row.suppressed.toLocaleString(),
  },
  {
    header: "Error",
    accessor: "error",
    render: (_v, row) =>
      row.error ? <span className={styles.runError}>{row.error}</span> : <span>—</span>,
  },
];

function SegmentDetail() {
  const {
    publicationId = "",
    edition = "",
    groupId = "",
    campaignId = "",
    segmentId = "",
  } = useParams<{
    publicationId: string;
    edition: string;
    groupId: string;
    campaignId: string;
    segmentId: string;
  }>();

  const navigate = useNavigate();

  const [segment, setSegment] = useState<SegmentWithProgress | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const campaignHref = ROUTES.surveys.campaignDetailPage(
    publicationId,
    edition,
    groupId,
    campaignId,
  );

  const refreshSegment = useCallback(async () => {
    setSegment(await campaignsService.getSegment(campaignId, segmentId));
  }, [campaignId, segmentId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [loadedSegment, loadedCampaign] = await Promise.all([
        campaignsService.getSegment(campaignId, segmentId),
        campaignsService.getById(campaignId),
      ]);
      setSegment(loadedSegment);
      setCampaign(loadedCampaign);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load this send.");
    } finally {
      setLoading(false);
    }
  }, [campaignId, segmentId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll every ~2s while the send is in flight so the counts tick live; stop as
  // soon as it is terminal (transient poll errors are swallowed so a blip
  // doesn't wipe the last good data).
  const inFlight = segment ? isInFlight(segment.status) : false;
  useEffect(() => {
    if (!inFlight) return;
    const interval = setInterval(() => {
      refreshSegment().catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [inFlight, refreshSegment]);

  const handleResume = useCallback(async () => {
    setResuming(true);
    try {
      const { resumedRecipients } = await campaignsService.resumeSegment(campaignId, segmentId);
      setToast({ message: segmentResumedMessage(resumedRecipients), type: "success" });
      await refreshSegment();
    } catch (err) {
      setToast({ message: segmentResumeErrorMessage(err), type: "error" });
    } finally {
      setResuming(false);
    }
  }, [campaignId, refreshSegment, segmentId]);

  const handleCopy = useCallback(async () => {
    if (!segment) return;
    const details = segmentCopyDetails(campaign?.name ?? campaignId, segment);
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      setToast({ message: "Details copied.", type: "success" });
    } catch {
      setToast({
        message: "Could not reach the clipboard — copy the details below.",
        type: "error",
      });
    }
  }, [campaign, campaignId, segment]);

  const header = (
    <PageHeader
      title={segment?.label ?? "Send"}
      subtitle={campaign ? `${campaign.name} — send details` : "Send details"}
    >
      <Button
        variant="outlined"
        color="primary"
        size="small"
        startIcon={<Icon icon="chevron-left" size="small" />}
        onClick={() => navigate(campaignHref)}
      >
        Back to campaign
      </Button>
    </PageHeader>
  );

  if (loading) {
    return (
      <div className={styles.wrapper}>
        {header}
        <LoadingState message="Loading send..." />
      </div>
    );
  }

  if (error || !segment) {
    return (
      <div className={styles.wrapper}>
        {header}
        <ErrorBanner error={error ?? "Send not found."} onRetry={load} loading={loading} />
      </div>
    );
  }

  const chip = segmentDisplayChip(segment);
  const schedule = segmentScheduleDisplay(segment);
  const exceptions = segmentExceptionLines(segment.counts);

  return (
    <div className={styles.wrapper}>
      {header}

      <div className={styles.recipientsHeaderRow}>
        <span className={styles.statusChip}>
          <Chip
            size="small"
            color={chip.color}
            className={chip.color === "default" ? styles.neutralStatusChip : undefined}
          >
            <span className={styles.statusChip}>
              <span
                className={styles.statusDot}
                style={{ backgroundColor: chip.dotColor }}
                aria-hidden="true"
              />
              {chip.label}
            </span>
          </Chip>
          <span className={styles.mutedNote}>{schedule.text}</span>
        </span>
      </div>

      <p className={styles.sendHeadline}>{segmentEmailedHeadline(segment.counts)}</p>

      {exceptions.length > 0 && (
        <ul className={styles.exceptionList}>
          {exceptions.map(line => (
            <li
              key={line.kind}
              className={
                line.kind === "unconfirmed" ? styles.exceptionRowAttention : styles.exceptionRow
              }
            >
              <div className={styles.exceptionText}>
                <span className={styles.exceptionHeadline}>{line.text}</span>
                {line.note && <span className={styles.exceptionNote}>{line.note}</span>}
              </div>

              {line.kind === "not_emailed_yet" && segment.can_resume && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={resuming}
                  onClick={() => void handleResume()}
                >
                  {resuming ? "Continuing…" : "Continue sending"}
                </Button>
              )}

              {line.kind === "unconfirmed" && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => void handleCopy()}
                >
                  {copied ? "Details copied" : "Copy details for the team"}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <details className={styles.historyDisclosure}>
        <summary className={styles.historySummary}>
          History ({segment.runs.length.toLocaleString()})
        </summary>
        <div className={styles.tableContainer}>
          <DataTable
            data={segment.runs}
            columns={runColumns}
            rowKey="id"
            emptyMessage="Nothing has run for this send yet."
          />
        </div>
      </details>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default SegmentDetail;
